import React, { useState } from 'react';
import { AnalyticsLocationProvider } from 'latitude-analytics';
import styled from 'styled-components';
import AnimatedNumber from 'react-animated-number';
import { Text } from '@latitude/text';
import { Box, Flex } from '@latitude/box';
import { redirectUrl } from '@/components/EstimateRateWidget/utils';
import { formatAsCurrency } from '../../utils';
import {
  MARGIN,
  COLOR,
  JUSTIFY_CONTENT,
  ALIGN,
  FLEX_DIRECTION,
  BREAKPOINT,
  LOAN_PAY_FREQUENCY,
  ALIGN_ITEMS,
  FONT_SIZE,
  CALCULATOR_TRACKING_V2
} from '../../utils/constants';
import FeeBreakDownModal from './FeeBreakdownModal';
import Typography from '@latitude/typography';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';
import Button from '@latitude/button-rebrand';

const INITIAL_LOAN_AMOUNT = 20000;
const INITIAL_INTEREST_RATE = 16;
const INITIAL_LOAN_TERM = 7;
const INITIAL_FREQ = 'WEEKLY';
const INITIAL_IS_VARIABLE = true;
const INITIAL_IS_SECURED = true;
const SESSION_STORAGE_KEY = 'personal-loan-calculator-values';

const REPAYMENT_FREQ_TEXT = {
  [LOAN_PAY_FREQUENCY.WEEKLY]: 'week',
  [LOAN_PAY_FREQUENCY.FORTNIGHTLY]: 'fortnight',
  [LOAN_PAY_FREQUENCY.MONTHLY]: 'month'
};
const CTA_BUTTON_LABEL = 'Get your personalised rate';

const animatedNumbersDuration = 300;

const StyledCalculatorDisplay = styled(Flex)`
  flex-direction: ${FLEX_DIRECTION.COLUMN};
  background: ${REBRAND_COLOR.INDIGO};
  border-radius: 0 0 0 60px;
  height: fit-content;
  margin-top: 24px;

  @media (min-width: ${BREAKPOINT.LG}) {
    width: 50%;
    margin-top: 0;
  }
  .typography-base__body--small {
    font-weight: 500;
  }
`;

const StyledFlex = styled(Flex)`
  text-align: center;
`;

const AmountsSavedFlex = styled(Flex)`
  flex-grow: 1;
  text-align: center;
  padding: 40px 0;
  @media (max-width: ${BREAKPOINT.LG}) {
    padding: 18px 0;
  }
`;

const StyledCalculatorTotalsBox = styled(Box)`
  margin: 0 auto;
  text-align: center;
  width: 300px !important;
  @media (max-width: ${BREAKPOINT.LG}) {
    width: 100% !important;
  }
`;

const getSessionData = () => {
  let data = null;

  try {
    const jsonText = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (jsonText) {
      data = JSON.parse(jsonText);
    }
  } catch (err) {
    console.error('Failed to read session storage', err);
  }

  if (!data) {
    console.log('No calculator session data');
    const sessionData = {
      amount: INITIAL_LOAN_AMOUNT,
      freq: INITIAL_FREQ,
      rate: INITIAL_INTEREST_RATE,
      isvariable: INITIAL_IS_VARIABLE,
      years: INITIAL_LOAN_TERM,
      isSecured: INITIAL_IS_SECURED
    };
    return sessionData;
  }

  const sessionData = {
    amount: data.loanAmount || INITIAL_LOAN_AMOUNT,
    freq: data.repaymentFreq || INITIAL_FREQ,
    rate: data.interestRate || INITIAL_INTEREST_RATE,
    isvariable: data.isVariable || INITIAL_IS_VARIABLE,
    years: data.repaymentYears || INITIAL_LOAN_TERM,
    isSecured: data.isSecured || INITIAL_IS_SECURED
  };
  return sessionData;
};

const IntroBox = styled(Box)``;

function defaultIntro() {
  return (
    <IntroBox>
      <Typography.SH4 color={REBRAND_COLOR.WHITE}>
        Estimated repayment including typical fees
      </Typography.SH4>
    </IntroBox>
  );
}

function formatPeriod(repaymentPeriod) {
  const result = REPAYMENT_FREQ_TEXT[repaymentPeriod];
  return result ? result : '(unknown)';
}

function CalculatorTotals({
  salesMode,
  repaymentPeriod,
  repaymentAmount,
  isVariable,
  isSecured,
  totalInterestPaid,
  totalAmountPaid,
  timeSavedMonths,
  timeSavedYears,
  applyUrl,
  applyCTATrackId,
  validationError,
  renderIntro = defaultIntro,
  ctaButtonLabel = CTA_BUTTON_LABEL,
  broker,
  loans1,
  isLefWaiver
}) {
  const [showFeesModal, setShowFeesModal] = useState(false);

  const repaymentPeriodText = formatPeriod(repaymentPeriod);

  return (
    <StyledCalculatorDisplay className="lfs-calculator-display-v2">
      {validationError ? (
        <StyledFlex
          justifyContent={JUSTIFY_CONTENT.CENTER}
          flexDirection={FLEX_DIRECTION.COLUMN}
          alignItems={ALIGN_ITEMS.CENTER}
          css={`
            width: 300px;
            padding: 48px 0px;
            gap: 40px;
            margin: 0 auto;
            @media (max-width: ${BREAKPOINT.LG}) {
              width: 100%;
              padding: 36px 24px;
              gap: 18px;
            }
          `}
        >
          <Typography.CTARegular color={REBRAND_COLOR.WHITE}>
            We’re unable to make a calculation for this amount.
          </Typography.CTARegular>
          <Flex
            justifyContent={JUSTIFY_CONTENT.CENTER}
            width="300px"
            margin="0 auto"
          >
            {/* Only show apply CTA if not in sales mode */}
            {!salesMode && (
              <Box width="100%">
                <AnalyticsLocationProvider location="Calculate your repayments">
                  <Button
                    variant="primary"
                    disabled={true}
                    size="md"
                    ctaUrl={applyUrl || redirectUrl('au', loans1)}
                    ctaText={ctaButtonLabel}
                    target="_self"
                    trackId={applyCTATrackId}
                    trackEventData={{
                      category: 'cta',
                      action: 'quote-link',
                      label: ctaButtonLabel,
                      location: 'personal-loan-calculator-totals'
                    }}
                    trackProductId={['PLAULF-WEB']}
                    css={{
                      width: '100%'
                    }}
                  />
                </AnalyticsLocationProvider>
              </Box>
            )}
          </Flex>
          <Text align={ALIGN.CENTER}>
            <Typography.Body small color={REBRAND_COLOR.WHITE}>
              No impact on your credit score.
            </Typography.Body>
          </Text>
        </StyledFlex>
      ) : (
        <>
          <StyledFlex
            justifyContent={JUSTIFY_CONTENT.CENTER}
            flexDirection={FLEX_DIRECTION.COLUMN}
            css={`
              padding: 48px 0px 24px 0px;
              @media (max-width: ${BREAKPOINT.LG}) {
                padding: 36px 24px;
                width: 100%;
              }
            `}
          >
            <div
              css={`
                max-width: 360px !important;
                margin: 0 auto;
                @media (max-width: ${BREAKPOINT.LG}) {
                  width: 100%;
                }
              `}
            >
              <StyledCalculatorTotalsBox>
                {renderIntro()}
                <div
                  css={`
                    margin: 16px 0px;
                  `}
                >
                  <Typography.N3 color={REBRAND_COLOR.WHITE}>
                    <AnimatedNumber
                      value={repaymentAmount}
                      duration={animatedNumbersDuration}
                      stepPrecision={0}
                      formatValue={n => (
                        <>
                          <span
                            css={`
                              font-size: 50%;
                              vertical-align: super;
                            `}
                          >
                            $
                          </span>
                          {formatAsCurrency(n).replace('$', '') || '0'}
                        </>
                      )}
                    />
                    <span css={{ fontSize: '30%' }}>
                      {' / '}
                      {repaymentPeriodText}
                    </span>
                  </Typography.N3>
                </div>
              </StyledCalculatorTotalsBox>

              <Text
                color={COLOR.WHITE}
                fontSize={FONT_SIZE.LARGE}
                align={ALIGN.CENTER}
                css={`
                  white-space: nowrap;
                  && a {
                    border-color: ${REBRAND_COLOR.WHITE};
                  }
                `}
              >
                <Typography.LinkRegular
                  color={REBRAND_COLOR.WHITE}
                  href="#"
                  onClick={e => {
                    setShowFeesModal(true);
                    e.preventDefault();
                  }}
                  trackEventData={{
                    category: CALCULATOR_TRACKING_V2.CATEGORY,
                    action: CALCULATOR_TRACKING_V2.ACTION,
                    location: CALCULATOR_TRACKING_V2.LOCATION,
                    label: 'View Fee Breakdown'
                  }}
                >
                  View Fee Breakdown
                </Typography.LinkRegular>
              </Text>

              <AmountsSavedFlex flexDirection={FLEX_DIRECTION.COLUMN}>
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '24px',
                    color: '#fff'
                  }}
                >
                  <div>
                    <Typography.Body color={REBRAND_COLOR.WHITE}>
                      Total interest paid
                      <br />
                    </Typography.Body>
                    <Typography.SH3 semibold color={REBRAND_COLOR.WHITE}>
                      <AnimatedNumber
                        value={totalInterestPaid}
                        duration={animatedNumbersDuration}
                        stepPrecision={0}
                        formatValue={n => (
                          <>
                            <span
                              css={`
                                font-size: 50%;
                                vertical-align: super;
                              `}
                            >
                              $
                            </span>
                            {formatAsCurrency(n).replace('$', '') || '0'}
                          </>
                        )}
                      />
                    </Typography.SH3>
                  </div>

                  <div
                    css={{
                      flex: '0 0 1px',
                      height: '48px',
                      marginTop: '6px',
                      backgroundColor: `${REBRAND_COLOR.WHITE}`,
                      opacity: 0.5
                    }}
                  />

                  <div>
                    <Typography.Body color={REBRAND_COLOR.WHITE}>
                      Total amount paid
                      <br />
                    </Typography.Body>
                    <Typography.SH3 semibold color={REBRAND_COLOR.WHITE}>
                      <AnimatedNumber
                        value={totalAmountPaid}
                        duration={animatedNumbersDuration}
                        stepPrecision={0}
                        formatValue={n => (
                          <>
                            <span
                              css={`
                                font-size: 50%;
                                vertical-align: super;
                              `}
                            >
                              $
                            </span>
                            {formatAsCurrency(n).replace('$', '') || '0'}
                          </>
                        )}
                      />
                    </Typography.SH3>
                  </div>
                </div>
                {/* {salesMode ? payItFaster : payItFasterLoans1} */}
              </AmountsSavedFlex>

              <Flex
                justifyContent={JUSTIFY_CONTENT.CENTER}
                width="300px"
                margin="0 auto"
                css={`
                  @media (max-width: ${BREAKPOINT.LG}) {
                    width: 100% !important;
                  }
                `}
              >
                {/* Only show apply CTA if not in sales mode */}
                {!salesMode && (
                  <Box width="100%">
                    <AnalyticsLocationProvider location="Calculate your repayments">
                      <Button
                        variant="primary"
                        size="md"
                        ctaUrl={applyUrl || redirectUrl('au', loans1)}
                        ctaText={ctaButtonLabel}
                        target="_self"
                        trackId={applyCTATrackId}
                        trackEventData={{
                          category: 'cta',
                          action: 'quote-link',
                          label: ctaButtonLabel,
                          location: CALCULATOR_TRACKING_V2.LOCATION,
                          value: JSON.stringify(getSessionData())
                        }}
                        trackProductId={['PLAULF-WEB']}
                        css={{
                          width: '100%'
                        }}
                      />
                    </AnalyticsLocationProvider>
                  </Box>
                )}
              </Flex>
              <Text marginTop={MARGIN.M16} align={ALIGN.CENTER}>
                <Typography.Body small color={REBRAND_COLOR.WHITE}>
                  No impact on your credit score.
                </Typography.Body>
              </Text>
            </div>
          </StyledFlex>
          <FeeBreakDownModal
            showFeesModal={showFeesModal}
            isLefWaiver={isLefWaiver}
            setShowFeesModal={setShowFeesModal}
            isVariable={isVariable}
            isSecured={isSecured}
            repaymentAmount={repaymentAmount}
            repaymentPeriod={repaymentPeriod}
          />
        </>
      )}
    </StyledCalculatorDisplay>
  );
}

export default CalculatorTotals;
